import { serialize } from 'object-to-formdata';
import { supportPlansViewTypes } from '@twiliointernal/services-common/containers/SupportPlansView';
import { ticketDetailsViewTypes } from '@twiliointernal/services-common/containers/TicketDetailsView';
import {
  Client,
  WiseOwlApi,
} from '@twiliointernal/org/twilio/e2-commons/shared/api';
import { getConfig } from '~/org/twilio/one-support-center/shared/osc-env';

import {
  AccountDetails,
  ApiResponse,
  ArticleByIdResponse,
  CategoriesResponse,
  CategoryByIdResponse,
  CreateTicketData,
  ErrorApiResponse,
  EscalateTicketProps,
  GetTicketQueryParams,
  GetSupportPlanResponse,
  GetUser,
  SectionByIdResponse,
  SearchResultsResponseData,
  SearchSuggestionResults,
  SendUserFeedbackResponse,
  SupportApiResponse,
  TicketFieldByIdData,
  UpdateTicketArgs,
  TicketHistoryResponse,
  TicketSubmissionData,
  UpdateSupportPlanArgs,
  UserDetails,
  UserFeedbackArgs,
  SystemStatusResponse,
  UserAccountsResponse,
  GetSubAccountsResponse,
  WiseOwlCreateUpdateBodyOsc,
  WiseOwlIngress,
  WiseOwlCreateUpdateBodyOscUngated,
  WiseOwlGetChatsByUserResponseData,
  WiseOwlCreateChatResponseData,
  WiseOwlUpdateChatResponseData,
  WiseOwlCreateFeedbackBody,
  WiseOwlCreateFeedbackBodyUngated,
  WiseOwlGetChatByIdResponseData,
  WiseOwlGetChatSummaryByIdResponseData,
  WiseOwlGetUngatedChatSummaryBody,
} from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

import Segment from './segment';
import { DEFAULT_HEADERS, LOG_PREFIX } from '../definitions/constants';
import {
  GetCategoryByIdProps,
  GetSectionsByIdProps,
} from '../definitions/types';

import { getRecaptchaToken } from '../utils/recaptcha';
import { RecaptchaAction } from '../utils/definitions/types';
import logger from '../../lib/logger';

const { searchIndex, supportApiEndpoint } = getConfig();

class SupportApi extends Client {
  user?: UserDetails;
  account?: AccountDetails;
  accounts?: AccountDetails[];
  subaccountsMetadata?: GetSubAccountsResponse;
  isOnSubaccount: boolean;
  wiseOwl: WiseOwlApi;

  constructor() {
    super(supportApiEndpoint);
    this.account = undefined;
    this.user = undefined;
    this.accounts = undefined;
    this.subaccountsMetadata = undefined;
    this.isOnSubaccount = false;
    this.wiseOwl = new WiseOwlApi(`${supportApiEndpoint}/wiseOwl`);
  }

  setUser = (user: UserDetails): void => {
    this.user = user;
  };

  setAccount = (account: AccountDetails): void => {
    this.account = account;
  };

  setAccounts = (accounts: AccountDetails[]): void => {
    this.accounts = accounts;
  };

  setSubaccountsMetadata = (
    subaccountsMetadata: GetSubAccountsResponse,
    isOnSubaccount: boolean,
  ): void => {
    this.subaccountsMetadata = subaccountsMetadata;
    this.isOnSubaccount = isOnSubaccount;
  };

  createGatedTicket = async (
    data: TicketSubmissionData,
  ): Promise<SupportApiResponse<CreateTicketData>> => {
    const csrf = await this.getCsrf();

    const formData: FormData = serialize(data, {
      noFilesWithArrayNotation: true,
      noAttributesWithArrayNotation: true,
    });

    const response: ApiResponse<CreateTicketData> = await this.post({
      path: '/tickets',
      body: formData,
      headers: {
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[createGatedTicket]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<CreateTicketData>;
  };

  createUngatedTicket = async (
    data: TicketSubmissionData,
  ): Promise<SupportApiResponse<CreateTicketData>> => {
    const csrf = await this.getCsrf();
    const stargateUngatedUserId = Segment.getAnonymousUserId();
    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);

    const formData: FormData = serialize(
      {
        ...data,
        recaptchaToken,
        stargateUngatedUserId,
      },
      {
        noFilesWithArrayNotation: true,
        noAttributesWithArrayNotation: true,
      },
    );

    const response: ApiResponse<CreateTicketData> = await this.post({
      path: '/tickets/ungated',
      body: formData,
      headers: {
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[createUngatedTicket]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<CreateTicketData>;
  };

  getTicketDetails = async (
    ticketId: string,
  ): Promise<SupportApiResponse<ticketDetailsViewTypes.TicketDetails>> => {
    const csrf = await this.getCsrf();

    const response: ApiResponse<ticketDetailsViewTypes.TicketDetails> =
      await this.get({
        path: `/tickets/${ticketId}?include=users`,
        headers: {
          'x-twilio-csrf': csrf,
        },
        requestOptions: {
          credentials: 'include',
        },
      });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getTicketDetails]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<ticketDetailsViewTypes.TicketDetails>;
  };

  updateTicket = async ({
    ticketId,
    data,
  }: UpdateTicketArgs): Promise<
    SupportApiResponse<ticketDetailsViewTypes.TicketDetails>
  > => {
    const csrf = await this.getCsrf();

    const formData: FormData = serialize(data, {
      noFilesWithArrayNotation: true,
      noAttributesWithArrayNotation: true,
    });

    logger.info(`${LOG_PREFIX}::[updateTicket]::[request]::${ticketId}`, {
      body: data,
      headers: {
        'x-twilio-csrf': csrf,
      },
    });

    const response: ApiResponse<ticketDetailsViewTypes.TicketDetails> =
      await this.post({
        path: `/tickets/${ticketId}?include=users`,
        body: formData,
        headers: {
          'x-twilio-csrf': csrf,
        },
        requestOptions: {
          credentials: 'include',
        },
      });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[updateTicket]::${ticketId}::${JSON.stringify(
          errorResponse,
        )}`,
        {
          errorResponse: JSON.stringify(errorResponse),
        },
      );
    }

    logger.info(`${LOG_PREFIX}::[updateTicket]::[response]::${ticketId}`, {
      response: JSON.stringify(response),
    });

    return response as SupportApiResponse<ticketDetailsViewTypes.TicketDetails>;
  };

  escalateTicket = async ({
    ticketId,
    escalationType,
    additionalInfo,
  }: EscalateTicketProps): Promise<
    SupportApiResponse<ticketDetailsViewTypes.TicketDetails>
  > => {
    const body = JSON.stringify({
      escalationType,
      escalationReason: additionalInfo,
    });
    const csrf = await this.getCsrf();

    const response: ApiResponse<ticketDetailsViewTypes.TicketDetails> =
      await this.put({
        path: `/tickets/escalations/${ticketId}`,
        body,
        headers: {
          ...DEFAULT_HEADERS,
          'x-twilio-csrf': csrf,
        },
        requestOptions: {
          credentials: 'include',
        },
      });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[escalateTicket]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<ticketDetailsViewTypes.TicketDetails>;
  };

  getUser = async (): Promise<SupportApiResponse<GetUser>> => {
    /**
     * The 'redirect' option needs to stay as 'follow' to prevent the following:
     *   - 'error': Throws console error on 302 response
     *   - 'manual': Allows handling of 302 response, but causes network error
     *   - 'redirect': Magically does not redirect and stays on OSC ¯\_(ツ)_/¯
     */
    const response: ApiResponse<GetUser> = await this.get({
      path: '/user',
      requestOptions: {
        redirect: 'follow',
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getUser]::${JSON.stringify(errorResponse)}`,
        response,
      );
    }

    return response as SupportApiResponse<GetUser>;
  };

  getTicketField = async (
    fieldId: string,
  ): Promise<SupportApiResponse<TicketFieldByIdData>> => {
    const response: ApiResponse<TicketFieldByIdData> = await this.get({
      path: `/tickets/field/${fieldId}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getTicketField]::${JSON.stringify(errorResponse)}`,
      );
    }
    return response as SupportApiResponse<TicketFieldByIdData>;
  };

  sendUserFeedback = async ({
    queryId,
    resultId,
    relevanceValue,
    clickTime,
  }: UserFeedbackArgs): Promise<
    SupportApiResponse<SendUserFeedbackResponse>
  > => {
    const csrf = await this.getCsrf();
    const baseBody = {
      queryId,
      index: searchIndex,
      clickTime: clickTime || Date.now(),
    };

    const body = JSON.stringify(
      relevanceValue ? { ...baseBody, relevanceValue } : baseBody,
    );

    const response: ApiResponse<SendUserFeedbackResponse> = await this.post({
      path: `/search/${resultId}/feedback`,
      body: body,
      headers: {
        ...DEFAULT_HEADERS,
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[sendUserFeedback]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<SendUserFeedbackResponse>;
  };

  search = async (
    queryText: string,
  ): Promise<SupportApiResponse<SearchResultsResponseData>> => {
    const sanitzedQueryText = encodeURIComponent(queryText);
    const response: ApiResponse<SearchResultsResponseData> = await this.get({
      path: `/search?index=${searchIndex}&queryText=${sanitzedQueryText}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[search]::${JSON.stringify(errorResponse)}`,
        response,
      );
    }

    return response as SupportApiResponse<SearchResultsResponseData>;
  };

  searchSuggestions = async (
    queryText: string,
  ): Promise<SupportApiResponse<SearchSuggestionResults>> => {
    const sanitizedQueryText = encodeURIComponent(queryText);
    const response: ApiResponse<SearchSuggestionResults> = await this.get({
      path: `/search/suggestions/?index=${searchIndex}&queryText=${sanitizedQueryText}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[searchSuggestions]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<SearchSuggestionResults>;
  };

  getCategories = async (): Promise<SupportApiResponse<CategoriesResponse>> => {
    const response: ApiResponse<CategoriesResponse> = await this.get({
      path: '/knowledgeBase/categories',
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getCategories]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<CategoriesResponse>;
  };

  getCategoryById = async ({
    categoryId,
  }: GetCategoryByIdProps): Promise<
    SupportApiResponse<CategoryByIdResponse>
  > => {
    const response: ApiResponse<CategoryByIdResponse> = await this.get({
      path: `/knowledgeBase/categories/${categoryId}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getCategoryById]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<CategoryByIdResponse>;
  };

  getSectionById = async ({
    sectionId,
  }: GetSectionsByIdProps): Promise<
    SupportApiResponse<SectionByIdResponse>
  > => {
    const response: ApiResponse<SectionByIdResponse> = await this.get({
      path: `/knowledgeBase/sections/${sectionId}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getSectionById]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<SectionByIdResponse>;
  };

  getArticleById = async (
    id: string,
  ): Promise<SupportApiResponse<ArticleByIdResponse>> => {
    const response: ApiResponse<ArticleByIdResponse> = await this.get({
      path: `/knowledgeBase/articles/${id}`,
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getArticleById]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<ArticleByIdResponse>;
  };

  getSupportPlan = async (): Promise<
    SupportApiResponse<GetSupportPlanResponse>
  > => {
    const response: ApiResponse<GetSupportPlanResponse> = await this.get({
      path: '/supportPlans',
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getSupportPlan]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<GetSupportPlanResponse>;
  };

  updateSupportPlan = async (
    updateRequest: UpdateSupportPlanArgs,
  ): Promise<SupportApiResponse<supportPlansViewTypes.SupportPlanResponse>> => {
    const body = JSON.stringify(updateRequest);
    const csrf = await this.getCsrf();
    const headers = {
      'Content-type': 'application/json',
      'x-twilio-csrf': csrf,
    };

    const response: ApiResponse<supportPlansViewTypes.SupportPlanResponse> =
      await this.put({
        path: '/supportPlans',
        body,
        headers,
        requestOptions: {
          credentials: 'include',
        },
      });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[updateSupportPlan]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<supportPlansViewTypes.SupportPlanResponse>;
  };

  sendPersonalizedSupportRequest = async (
    formValues: supportPlansViewTypes.PersonalizedSupportFormValues,
  ): Promise<
    SupportApiResponse<supportPlansViewTypes.PersonalizedSupportFormValues>
  > => {
    const body = JSON.stringify(formValues);
    const csrf = await this.getCsrf();
    const headers = {
      'Content-type': 'application/json',
      'x-twilio-csrf': csrf,
    };

    const response: ApiResponse<supportPlansViewTypes.PersonalizedSupportFormValues> =
      await this.post({
        path: '/supportPlans/personalizedSupport',
        body,
        headers,
        requestOptions: {
          credentials: 'include',
        },
      });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[sendPersonalizedSupportRequest]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<supportPlansViewTypes.PersonalizedSupportFormValues>;
  };

  getTickets = async (
    queryParams: GetTicketQueryParams,
  ): Promise<SupportApiResponse<TicketHistoryResponse>> => {
    const response: ApiResponse<TicketHistoryResponse> = await this.get({
      path: `/tickets?q=${JSON.stringify(queryParams)}`,
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getTickets]::${JSON.stringify(errorResponse)}`,
        response,
      );
    }

    return response as SupportApiResponse<TicketHistoryResponse>;
  };

  getSystemStatus = async (): Promise<
    SupportApiResponse<SystemStatusResponse[]>
  > => {
    const response: ApiResponse<SystemStatusResponse[]> = await this.get({
      path: '/systemStatus',
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getSystemStatus]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<SystemStatusResponse[]>;
  };

  getUserAccounts = async (): Promise<
    SupportApiResponse<UserAccountsResponse>
  > => {
    const response: ApiResponse<UserAccountsResponse> = await this.get({
      path: '/user/accounts',
      requestOptions: {
        redirect: 'manual',
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getUserAccounts]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<UserAccountsResponse>;
  };

  /**
   * WISE OWL
   */
  getWiseOwlChatsByUser = async (): Promise<
    SupportApiResponse<WiseOwlGetChatsByUserResponseData>
  > => {
    const csrf = await this.getCsrf();

    const response = await this.wiseOwl.getChats({
      headers: {
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getWiseOwlChatsByUser]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlGetChatsByUserResponseData>;
  };

  getWiseOwlChatsById = async (
    chatId: string,
  ): Promise<SupportApiResponse<WiseOwlGetChatByIdResponseData>> => {
    const csrf = await this.getCsrf();

    const response = await this.wiseOwl.getChatById({
      chatId,
      headers: {
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getWiseOwlChatsById]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlGetChatByIdResponseData>;
  };

  createWiseOwlChat = async (
    input: string,
  ): Promise<SupportApiResponse<WiseOwlCreateChatResponseData>> => {
    const csrf = await this.getCsrf();

    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
    const body: WiseOwlCreateUpdateBodyOsc = {
      input,
      recaptchaToken,
      stream: true,
    };

    const response = await this.wiseOwl.createChat({
      ingress: WiseOwlIngress.osc,
      body,
      headers: {
        'x-twilio-csrf': csrf,
        'Content-type': 'application/json',
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[createWiseOwlChat]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<WiseOwlCreateChatResponseData>;
  };

  updateWiseOwlChat = async (
    chatId: string,
    input: string,
  ): Promise<SupportApiResponse<WiseOwlUpdateChatResponseData>> => {
    const csrf = await this.getCsrf();

    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
    const body: WiseOwlCreateUpdateBodyOsc = {
      input,
      stream: true,
      recaptchaToken,
    };

    const response = await this.wiseOwl.updateChat({
      chatId,
      ingress: WiseOwlIngress.osc,
      body,
      headers: {
        'x-twilio-csrf': csrf,
        'Content-type': 'application/json',
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[updateWiseOwlChat]::${JSON.stringify(errorResponse)}`,
      );
    }

    return response as SupportApiResponse<WiseOwlUpdateChatResponseData>;
  };

  createWiseOwlFeedback = async (
    chatId: string,
    messageId: string,
    feedback: string,
    positive: boolean,
  ): Promise<SupportApiResponse<{}>> => {
    const csrf = await this.getCsrf();

    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
    const body: WiseOwlCreateFeedbackBody = {
      feedback,
      positive,
      recaptchaToken,
    };

    const response = await this.wiseOwl.createFeedback({
      ingress: WiseOwlIngress.osc,
      chatId,
      messageId,
      body,
      headers: {
        'x-twilio-csrf': csrf,
        'Content-type': 'application/json',
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[createWiseOwlFeedback]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<{}>;
  };

  getWiseOwlChatSummaryById = async (
    chatId: string,
  ): Promise<SupportApiResponse<WiseOwlGetChatSummaryByIdResponseData>> => {
    const csrf = await this.getCsrf();

    const response = await this.wiseOwl.getChatSummaryById({
      chatId,
      headers: {
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[getWiseOwlChatSummaryById]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlGetChatSummaryByIdResponseData>;
  };

  ungatedCreateWiseOwlChat = async (
    input: string,
  ): Promise<SupportApiResponse<WiseOwlCreateChatResponseData>> => {
    const csrf = await this.getCsrf();
    const stargateUngatedUserId = Segment.getAnonymousUserId();

    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
    const body: WiseOwlCreateUpdateBodyOscUngated = {
      input,
      stream: true,
      recaptchaToken,
      stargateUngatedUserId,
    };

    const response = await this.wiseOwl.ungatedCreateChat({
      ingress: WiseOwlIngress.oscUngated,
      body,
      headers: {
        'x-twilio-csrf': csrf,
        'Content-type': 'application/json',
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[ungatedCreateWiseOwlChat]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlCreateChatResponseData>;
  };

  ungatedUpdateWiseOwlChat = async (
    chatId: string,
    input: string,
  ): Promise<SupportApiResponse<WiseOwlUpdateChatResponseData>> => {
    const csrf = await this.getCsrf();
    const stargateUngatedUserId = Segment.getAnonymousUserId();

    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
    const body: WiseOwlCreateUpdateBodyOscUngated = {
      input,
      stream: true,
      recaptchaToken,
      stargateUngatedUserId,
    };

    const response = await this.wiseOwl.ungatedUpdateChat({
      chatId,
      ingress: WiseOwlIngress.oscUngated,
      body,
      headers: {
        'x-twilio-csrf': csrf,
        'Content-type': 'application/json',
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[ungatedUpdateWiseOwlChat]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlUpdateChatResponseData>;
  };

  ungatedCreateWiseOwlFeedback = async (
    chatId: string,
    messageId: string,
    feedback: string,
    positive: boolean,
  ): Promise<SupportApiResponse<{}>> => {
    const csrf = await this.getCsrf();
    const stargateUngatedUserId = Segment.getAnonymousUserId();

    const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
    const body: WiseOwlCreateFeedbackBodyUngated = {
      feedback,
      positive,
      recaptchaToken,
      stargateUngatedUserId,
    };

    const response = await this.wiseOwl.ungatedCreateFeedback({
      ingress: WiseOwlIngress.oscUngated,
      chatId,
      messageId,
      body,
      headers: {
        'x-twilio-csrf': csrf,
        'Content-type': 'application/json',
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[ungatedCreateWiseOwlFeedback]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlCreateChatResponseData>;
  };

  ungatedGetWiseOwlChatSummaryById = async (
    chatId: string,
  ): Promise<SupportApiResponse<WiseOwlGetChatSummaryByIdResponseData>> => {
    const csrf = await this.getCsrf();
    const stargateUngatedUserId = Segment.getAnonymousUserId();

    const body: WiseOwlGetUngatedChatSummaryBody = {
      stargateUngatedUserId,
    };

    const response = await this.wiseOwl.ungatedGetChatSummaryById({
      chatId,
      body,
      headers: {
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${LOG_PREFIX}::[ungatedGetWiseOwlChatSummaryById]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as SupportApiResponse<WiseOwlGetChatSummaryByIdResponseData>;
  };
}

export default new SupportApi();
